import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import Button from "../../../Button";
import Input, { MaterialInputEvent } from "../../Input";
import Select, { MaterialSelectEvent } from "../../Select";
import Snackbar from "../../Snackbar";
import Table, { TableColumn } from "../../Table";
import {
  AdminUsersApi,
  AdminRole,
  AdminUser,
  AdminUsersList,
  UserStatusFilter,
} from "../../../../services/AdminUsersService";
import { getUid } from "../../../../services/AuthService";
import ConfirmDialog, { ConfirmAction } from "./ConfirmDialog";
import RoleTags from "./RoleTags";
import StatusTag from "./StatusTag";
import UserFormDialog from "./UserFormDialog";

import { useUserManagementTranslation, UserManagementResource } from "./useUserManagementTranslation";

import style from "./styles.module.scss";

const keyI18n = "translations.admin.users";
const STATUSES: UserStatusFilter[] = ["active", "inactive", "all"];
const SEARCH_DELAY = 300;

type Status = "loading" | "ready" | "error";

interface Query {
  q: string;
  status: UserStatusFilter;
  roleId: number | null;
}

interface Confirmation {
  action: ConfirmAction;
  user: AdminUser;
}

interface Props {
  service: AdminUsersApi;
  resource: UserManagementResource;
}

export default function UserManagement({ service, resource }: Props) {
  const { t } = useUserManagementTranslation(resource);
  const history = useHistory();
  const { search } = useLocation();
  const query = useMemo(() => parseQuery(search), [search]);

  const [data, setData] = useState<AdminUsersList | null>(null);
  const [status, setStatus] = useState<Status>("loading");
  const [term, setTerm] = useState(query.q);
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const currentUid = (getUid() || "").toLowerCase();

  const updateQuery = useCallback((changes: Partial<Query>) => {
    history.replace({ search: serializeQuery({ ...parseQuery(history.location.search), ...changes }) });
  }, [history]);

  const load = useCallback(() => {
    setStatus("loading");
    service.list({ q: query.q || undefined, status: query.status, roleId: query.roleId || undefined })
      .then((list) => {
        setData(list);
        setStatus("ready");
      })
      .catch(() => setStatus("error"));
  }, [query, service]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    service.roles()
      .then((data) => setRoles(Array.isArray(data) ? data : []))
      .catch(() => setRoles([]));
  }, [service]);

  useEffect(() => {
    setTerm((current) => (current.trim() === query.q ? current : query.q));
  }, [query.q]);

  useEffect(() => {
    if (term.trim() === query.q) {
      return undefined;
    }

    const timer = window.setTimeout(() => updateQuery({ q: term.trim() }), SEARCH_DELAY);
    return () => window.clearTimeout(timer);
  }, [term, query.q, updateQuery]);

  const closeNotice = useCallback(() => setNotice(null), []);

  const isCurrentUser = (user: AdminUser) => user.email.toLowerCase() === currentUid;

  const handleSaved = (user: AdminUser, created: boolean) => {
    setCreating(false);
    setEditing(null);
    setNotice(t(`${keyI18n}.notices.${created ? "created" : "updated"}`, { name: user.name || user.email }));
    load();
  };

  const confirm = () => {
    if (!confirmation) {
      return;
    }

    const { action, user } = confirmation;
    const request = action === "deactivate"
      ? service.deactivate(user.id)
      : service.reactivate(user.id);

    setConfirming(true);
    setConfirmError(false);
    request
      .then(() => {
        setConfirmation(null);
        setNotice(t(`${keyI18n}.notices.${action}d`, { name: user.name || user.email }));
        load();
      })
      .catch(() => setConfirmError(true))
      .finally(() => setConfirming(false));
  };

  const openConfirmation = (action: ConfirmAction, user: AdminUser) => {
    setConfirmError(false);
    setConfirmation({ action, user });
  };

  const renderName = (user: AdminUser) => (
    <span className={style.name}>
      <span>{user.name || t(`${keyI18n}.noName`)}</span>
      {isCurrentUser(user) && (
        <span className={`pgm-label-medium ${style.youTag}`}>{t(`${keyI18n}.you`)}</span>
      )}
    </span>
  );

  const renderActions = (user: AdminUser) => {
    const own = isCurrentUser(user);
    const label = user.name || user.email;

    return (
      <span className={style.actions}>
        <Button
          iconButton
          variant="text"
          size="small"
          aria-label={t(`${keyI18n}.actions.editUser`, { name: label })}
          tooltip={t(`${keyI18n}.actions.edit`)}
          tooltipPlacement="top-end"
          onClick={() => setEditing(user)}
          icon={<span className="material-symbols-sharp">edit</span>}
        />
        {user.active ? (
          <Button
            iconButton
            variant="text"
            size="small"
            aria-label={own ? t(`${keyI18n}.actions.ownDeactivate`) : t(`${keyI18n}.actions.deactivateUser`, { name: label })}
            tooltip={own ? t(`${keyI18n}.actions.ownDeactivate`) : t(`${keyI18n}.actions.deactivate`)}
            tooltipPlacement="top-end"
            softDisabled={own}
            onClick={own ? undefined : () => openConfirmation("deactivate", user)}
            icon={<span className="material-symbols-sharp">person_off</span>}
          />
        ) : (
          <Button
            iconButton
            variant="text"
            size="small"
            aria-label={t(`${keyI18n}.actions.reactivateUser`, { name: label })}
            tooltip={t(`${keyI18n}.actions.reactivate`)}
            tooltipPlacement="top-end"
            onClick={() => openConfirmation("reactivate", user)}
            icon={<span className="material-symbols-sharp">person_check</span>}
          />
        )}
      </span>
    );
  };

  const columns: TableColumn<AdminUser>[] = [
    { key: "name", header: t(`${keyI18n}.columns.name`), render: renderName },
    { key: "email", header: t(`${keyI18n}.columns.email`), render: (user) => <span className={style.email}>{user.email}</span> },
    { key: "roles", header: t(`${keyI18n}.columns.roles`), render: (user) => <RoleTags roles={user.roles} /> },
    { key: "status", header: t(`${keyI18n}.columns.status`), render: (user) => <StatusTag active={user.active} /> },
    { key: "actions", header: t(`${keyI18n}.columns.actions`), render: renderActions, align: "end", width: "112px", srOnlyHeader: true },
  ];

  const renderCompactRow = (user: AdminUser) => (
    <div className={style.compactRow}>
      <div className={style.compactMain}>
        <span className={`pgm-title-medium ${style.compactName}`}>{renderName(user)}</span>
        <span className={`pgm-body-medium ${style.email}`}>{user.email}</span>
        <span className={style.compactTags}>
          <StatusTag active={user.active} />
          <RoleTags roles={user.roles} />
        </span>
      </div>
      {renderActions(user)}
    </div>
  );

  const emptyState = query.q ? (
    <div className={style.empty}>
      <span aria-hidden="true" className={`material-symbols-sharp ${style.emptyIcon}`}>search_off</span>
      <p className={`pgm-title-medium ${style.emptyText}`}>{t(`${keyI18n}.emptySearch`, { term: query.q })}</p>
      <Button variant="text" size="small" onClick={() => setTerm("")}>
        {t(`${keyI18n}.clearSearch`)}
      </Button>
    </div>
  ) : (
    <div className={style.empty}>
      <span aria-hidden="true" className={`material-symbols-sharp ${style.emptyIcon}`}>group</span>
      <p className={`pgm-title-medium ${style.emptyText}`}>{t(`${keyI18n}.empty.${query.status}`)}</p>
    </div>
  );

  const statusOptions = STATUSES.map((value) => ({ value, label: t(`${keyI18n}.status.filter.${value}`) }));
  const roleOptions = [
    { value: "", label: t(`${keyI18n}.role.all`) },
    ...roles.map((role) => ({ value: String(role.id), label: role.name })),
  ];

  return (
    <div className={style.layout}>
      <header className={style.header}>
        <div className={style.heading}>
          <h1 className={`pgm-headline-small ${style.title}`}>{t(`${keyI18n}.title`)}</h1>
          <p className={`pgm-body-medium ${style.subtitle}`}>{t(`${keyI18n}.subtitle`)}</p>
        </div>
        <Button
          variant="filled"
          size="small"
          className={style.createButton}
          onClick={() => setCreating(true)}
          icon={<span className="material-symbols-sharp">person_add</span>}
        >
          {t(`${keyI18n}.actions.create`)}
        </Button>
      </header>

      <div className={style.filters} role="search">
        <Input
          variant="outlined"
          type="search"
          className={style.search}
          label={t(`${keyI18n}.search`)}
          value={term}
          clearButtonAriaLabel={t(`${keyI18n}.clearSearch`)}
          leadingIcon={<span className="material-symbols-sharp">search</span>}
          onInput={(event: MaterialInputEvent) => setTerm(event.currentTarget.value)}
        />
        <Select
          variant="outlined"
          className={style.statusFilter}
          label={t(`${keyI18n}.status.label`)}
          value={query.status}
          options={statusOptions}
          onChange={(event: MaterialSelectEvent) => updateQuery({ status: event.currentTarget.value as UserStatusFilter })}
        />
        <Select
          variant="outlined"
          className={style.roleFilter}
          label={t(`${keyI18n}.role.label`)}
          value={query.roleId ? String(query.roleId) : ""}
          options={roleOptions}
          onChange={(event: MaterialSelectEvent) => updateQuery({ roleId: Number(event.currentTarget.value) || null })}
        />
      </div>

      {status === "ready" && data && (
        <p className={`pgm-body-medium ${style.count}`} aria-live="polite">
          {t(`${keyI18n}.count`, { count: data.total })}
        </p>
      )}

      {status === "error" ? (
        <div className={style.error} role="alert">
          <span aria-hidden="true" className={`material-symbols-sharp ${style.emptyIcon}`}>error</span>
          <p className={`pgm-title-medium ${style.emptyText}`}>{t(`${keyI18n}.loadError`)}</p>
          <Button variant="outlined" size="small" onClick={load}>
            {t(`${keyI18n}.retry`)}
          </Button>
        </div>
      ) : (
        <Table
          caption={t(`${keyI18n}.caption`)}
          columns={columns}
          rows={data ? data.users : []}
          rowKey={(user) => user.id}
          loading={status === "loading"}
          loadingRows={5}
          emptyState={emptyState}
          renderCompactRow={renderCompactRow}
        />
      )}

      <UserFormDialog
        service={service}
        resource={resource}
        open={creating || editing !== null}
        user={editing}
        roles={roles}
        own={editing !== null && isCurrentUser(editing)}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSaved={handleSaved}
      />

      <ConfirmDialog
        resource={resource}
        action={confirmation ? confirmation.action : null}
        name={confirmation ? confirmation.user.name || confirmation.user.email : ""}
        busy={confirming}
        failed={confirmError}
        onCancel={() => setConfirmation(null)}
        onConfirm={confirm}
      />

      <Snackbar message={notice} onClose={closeNotice} />
    </div>
  );
}

function parseQuery(search: string): Query {
  const params = new URLSearchParams(search);
  const status = params.get("status") as UserStatusFilter | null;
  const roleId = Number(params.get("perfil"));

  return {
    q: (params.get("q") || "").trim(),
    status: status && STATUSES.includes(status) ? status : "active",
    roleId: Number.isInteger(roleId) && roleId > 0 ? roleId : null,
  };
}

function serializeQuery({ q, status, roleId }: Query): string {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (status !== "active") params.set("status", status);
  if (roleId) params.set("perfil", String(roleId));
  const value = params.toString();
  return value ? `?${value}` : "";
}
